import * as React from "react";
import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import Person4Icon from "@mui/icons-material/Person4";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import IconButton from "@mui/material/IconButton";
import ArticleIcon from "@mui/icons-material/Article";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import CategoryIcon from "@mui/icons-material/Category";
import VerifiedIcon from "@mui/icons-material/Verified";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import HelpIcon from "@mui/icons-material/Help";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import { People, SupportAgent } from "@mui/icons-material";
import DiscountIcon from "@mui/icons-material/Discount";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "./Sidebar.css";
import NotificationModal from "./Notification";
import Menu from "./Menu";
import logo from "../../assets/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { nfcount, nfdata } from "../../service/Mainservice";
import { Badge } from "@mui/material";
import { useAuth } from "../../context/AuthContext";

const drawerWidth = 250;

// Menu structure
const menuItems = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: "/Dashboard",
    permission: "view_reports",
  },
  {
    label: "Orders",
    icon: <ListAltIcon />,
    permission: "manage_orders",
    subItems: [
      { label: "All Orders", path: "/Orders", permission: "manage_orders" },
      {
        label: "Order Details",
        path: "/Order_Details",
        permission: "manage_orders",
      },
    ],
  },
  {
    label: "Users (Customers)",
    icon: <People />,
    permission: "manage_team",
    subItems: [
      { label: "All Users", path: "/Users_List", permission: "manage_team" },
      {
        label: "User Details",
        path: "/User_Details",
        permission: "manage_team",
      },
    ],
  },
  {
    label: "Stores (Providers)",
    icon: <Person4Icon />,
    permission: "manage_stores",
    subItems: [
      {
        label: "All Stores",
        path: "/Stores_List",
        permission: "manage_stores",
      },
      {
        label: "Store Details",
        path: "/Store_Details",
        permission: "manage_stores",
      },
      {
        label: "Store Requests",
        path: "/RequestofStores",
        permission: "approve_stores",
      },
    ],
  },
  {
    label: "Drivers",
    icon: <DeliveryDiningIcon />,
    permission: "manage_drivers",
    subItems: [
      { label: "All Drivers", path: "/Drivers", permission: "manage_drivers" },
      {
        label: "Driver Requests",
        path: "/RequestofDrivers",
        permission: "approve_drivers",
      },
      {
        label: "Driver Details",
        path: "/Driver_Details",
        permission: "manage_drivers",
      },
    ],
  },
  {
    label: "Products",
    icon: <Inventory2Icon />,
    permission: "manage_products", // parent permission (if any sub-item also has permission)
    subItems: [
      { label: "Products", path: "/Products", permission: "manage_products" },
      {
        label: "Categories",
        path: "/Categories_List",
        permission: "manage_catalog",
      },
      {
        label: "Subcategories",
        path: "/SubCategories_List",
        permission: "manage_catalog",
      },
    ],
  },
  {
    label: "Finance",
    icon: <LocalAtmIcon />,
    permission: "manage_finance",
    subItems: [
      {
        label: "Revenue Overview",
        path: "/finance/revenue",
        permission: "manage_finance",
      },
      {
        label: "Store Payouts",
        path: "/finance/store-payouts",
        permission: "manage_finance",
      },
      {
        label: "Driver Payouts",
        path: "/finance/driver-payouts",
        permission: "manage_finance",
      },
      {
        label: "Transactions",
        path: "/finance/transactions",
        permission: "manage_finance",
      },
      {
        label: "Tax Documents",
        path: "/finance/tax",
        permission: "manage_finance",
      },
    ],
  },
  {
    label: "Promotions & Marketing",
    icon: <DiscountIcon />,
    permission: "manage_marketing",
    subItems: [
      {
        label: "Promotions",
        path: "/promotions/promotions",
        permission: "manage_marketing",
      },
      {
        label: "Banners",
        path: "/promotions/banners",
        permission: "manage_marketing",
      },
      {
        label: "Announcements",
        path: "/promotions/announcements",
        permission: "manage_marketing",
      },
      {
        label: "Referrals",
        path: "/promotions/referrals",
        permission: "manage_marketing",
      },
    ],
  },
  {
    label: "Support",
    icon: <SupportAgent />,
    permission: "manage_support",
    subItems: [
      {
        label: "Tickets",
        path: "/support/tickets",
        permission: "manage_support",
      },
      {
        label: "FAQs",
        path: "/FAQS_list",
        permission: "manage_support",
      },
      {
        label: "Live Chat",
        path: "/support/chats",
        permission: "manage_support",
      },
      {
        label: "Disputes",
        path: "/support/disputes",
        permission: "manage_support",
      },
      {
        label: "Safety Incidents",
        path: "/support/safety",
        permission: "manage_support",
      },
    ],
  },
  {
    label: "Reports & Analytics",
    icon: <AnalyticsIcon />,
    path: "/Reports",
    permission: "view_reports",
  },
  {
    label: "Admin Team",
    icon: <People />,
    permission: "manage_team",
    subItems: [
      {
        label: "Admin List",
        path: "/admin-team/list",
        permission: "manage_team",
      },
      {
        label: "Add Admin",
        path: "/admin-team/add",
        permission: "manage_team",
      },
      {
        label: "Roles & Permissions",
        path: "/admin-team/roles",
        permission: "manage_team",
      },
      {
        label: "Activity Log",
        path: "/admin-team/activity",
        permission: "manage_team",
      },
      {
        label: "Two Factor Authentication",
        path: "/admin-team/2fa",
        permission: "manage_team",
      },
    ],
  },
  {
    label: "Settings",
    icon: <SettingsIcon />,
    path: "/Settings",
    permission: "manage_settings",
  },
];

function ResponsiveDrawer(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shownotification, setshownotification] = useState(false);
  const [showmenu, setshowmenu] = useState(false);
  const [count, setcount] = useState(0);
  const [nf, setnf] = useState();
  const [expandedMenus, setExpandedMenus] = useState({});
  const { permissions, loading } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const menuref = useRef();
  const profile = JSON.parse(localStorage.getItem("Data"))?.image;

  useEffect(() => {
    getcount();
    let handler = (event) => {
      if (!menuref.current.contains(event.target)) {
        setshownotification(false);
        setshowmenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  useEffect(() => {
    // Auto-expand parent menus if a sub-item is active
    const newExpanded = { ...expandedMenus };
    menuItems.forEach((item, index) => {
      if (item.subItems) {
        const isActive = item.subItems.some(
          (sub) => location.pathname === sub.path,
        );
        if (isActive) {
          newExpanded[index] = true;
        }
      }
    });
    setExpandedMenus(newExpanded);
  }, [location.pathname]);

  const getcount = async () => {
    try {
      const response = await nfcount();
      setcount(response.data.data);
    } catch (error) {
      if (error.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await nfdata();
      setnf(response.data.data);
    } catch (error) {
      if (error.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSubMenu = (index) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const isActive = (path) => location.pathname === path;
  const isSubActive = (subItems) => {
    if (!subItems) return false;
    return subItems.some((sub) => location.pathname === sub.path);
  };

  const drawer = (
    <div className="Sidebar">
      <div className="Sidebar_logodiv">
        <img src={logo} className="sidebar_logo" alt="logo" />
      </div>
      <ul className="sidebar_alllist">
        {menuItems.map((item, index) => {
          const hasSub = item.subItems && item.subItems.length > 0;
          const isExpanded = expandedMenus[index];
          const isItemActive =
            isActive(item.path) || isSubActive(item.subItems);

          return (
            <li key={index} className="SidebarItemWrapper">
              <div
                className={isItemActive ? "Selectedsidebar" : "SidebarItem"}
                onClick={() => {
                  if (hasSub) {
                    toggleSubMenu(index);
                  } else {
                    navigate(item.path);
                    setMobileOpen(false);
                  }
                }}
              >
                {item.icon}
                <p className="sidebarlabel">{item.label}</p>
                {hasSub && (
                  <span className="expand_icon">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </span>
                )}
              </div>
              {hasSub && isExpanded && (
                <ul className="submenu_list">
                  {item.subItems.map((sub, subIndex) => (
                    <li
                      key={subIndex}
                      className={
                        isActive(sub.path)
                          ? "SubmenuItem Selected"
                          : "SubmenuItem"
                      }
                      onClick={() => {
                        navigate(sub.path);
                        setMobileOpen(false);
                      }}
                    >
                      <span className="submenu_bullet">•</span>
                      {sub.label}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <div ref={sidebarRef}>
      <div>
        <div className="appbar">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: "none" },
              color: "black",
            }}
          >
            <MenuIcon />
          </IconButton>
          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <Badge
              badgeContent={count}
              color="error"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setshownotification(true);
                fetchData();
              }}
            >
              <NotificationsNoneIcon color="action" sx={{ fontSize: "25px" }} />
            </Badge>
            <img
              alt="profileimg"
              src={profile}
              className="appbarprofileimg"
              onClick={() => setshowmenu(true)}
            />
          </div>
        </div>
        <Box
          component="nav"
          sx={{
            width: { md: drawerWidth },
            flexShrink: { md: 0 },
            zIndex: 0,
          }}
          aria-label="mailbox folders"
        >
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                background: "var(--red)",
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                border: "none",
                background: "var(--red)",
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      </div>
      <div ref={menuref}>
        {shownotification && <NotificationModal nf={nf} getcount={getcount} />}
        {showmenu && <Menu showmenu={setshowmenu} />}
      </div>
    </div>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};

export default ResponsiveDrawer;
